/**
 * Provides a date picker for Final Forms (using https://github.com/airbnb/react-dates)
 *
 * NOTE: If you are using this component inside BookingDatesForm,
 * you should convert value.date to start date and end date before submitting it to API
 */

import React, { Component } from 'react';
import { bool, func, object, oneOf, string, number } from 'prop-types';
import { isInclusivelyAfterDay, isInclusivelyBeforeDay } from 'react-dates';
import { Field } from 'react-final-form';
import classNames from 'classnames';
import moment from 'moment';

import { useConfiguration } from '../../context/configurationContext';
import { START_DATE, END_DATE } from '../../util/dates';
import { FieldSelect, FieldTextInput, ValidationError } from '../../components';

import DateRangeInput from './DateRangeInput';
import css from './FieldDateRangeInput.module.css';
import { maxQuantity } from '../../util/validators';

const MAX_MOBILE_SCREEN_WIDTH = 768;

class FieldDateRangeInputComponent extends Component {
  constructor(props) {
    super(props);
    this.state = { focusedInput: null };
    this.handleBlur = this.handleBlur.bind(this);
    this.handleFocus = this.handleFocus.bind(this);
    this.debounceTimer = null;
  };

  componentDidMount() {
    this.setState(prev => ({ ...prev, focusedInput: this.props.focusedInput }))
  };

  componentDidUpdate(prevProps) {
    // Update focusedInput in case a new value for it is
    // passed in the props. This may occur if the focus
    // is manually set to the date picker.
    //
    // Regression fix (manual QA): same bug, same fix as DateRangeInput.js's own
    // identical sync (see that file's comment for the full mechanism) - this used to
    // only mirror the prop into local state when the INCOMING value was truthy, so a
    // parent clearing focus back to undefined (BookingDatesForm.js does this the
    // instant an async prefill resolves) never reached this.state.focusedInput, which
    // is what actually gets passed on to the inner DateRangeInput below. Two layers
    // both had this exact asymmetry - fixing only one left the other still stuck.
    if (this.props.focusedInput !== prevProps.focusedInput) {
      this.setState({ focusedInput: this.props.focusedInput });
    }

    // Regression fix (manual QA, cart/storefront/rental-default prefill landing on an
    // empty calendar): handleFocus below starts a 300ms debounce timer the moment this
    // field's underlying react-dates picker reports itself focused - which happens on
    // this component's very first mount too, since BookingDatesForm seeds its
    // focusedInput state to START_DATE whenever nothing is known to prefill yet (see
    // that file's own startsCollapsed comment). If an async prefill (cart item/
    // storefront/rental defaults) resolves and calls setFocusedInput(undefined) to
    // correct that seed - which it does, synchronously, in the same tick it applies
    // the real dates - the PROP here does flip to falsy, but nothing previously told
    // this component's own pending debounce timer to stop: it fires anyway ~300ms
    // after the original mount-time focus, calling props.onFocus(START_DATE)
    // regardless of what's happened since. BookingDatesForm's onFocus handler treats
    // ANY focus of START_DATE as "user re-opened the calendar to change dates" and
    // wipes bookingDates + un-applies - discarding the prefill a few hundred
    // milliseconds after it correctly landed, with no user interaction at all. Once the
    // parent clears focusedInput, the timer's premise (this field is still focused) no
    // longer holds - the pending callback must be cancelled, not just outrun.
    if (prevProps.focusedInput && !this.props.focusedInput && this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  }

  handleBlur(focusedInput) {
    this.setState({ focusedInput: null });
    this.props.input.onBlur(focusedInput);
    // Notify the containing component that the focused
    // input has changed.
    if (this.props.onFocusedInputChange) {
      this.props.onFocusedInputChange(null);
    }
  }

  handleFocus(focusedInput) {
    clearTimeout(this.debounceTimer);

    // Notify the containing component immediately that the focused input has changed
    // This ensures the parent component can react instantly (e.g., hiding buttons on mobile)
    if (this.props.onFocusedInputChange) {
      this.props.onFocusedInputChange(focusedInput);
    }

    this.debounceTimer = setTimeout(() => {
      // The parent (BookingDatesForm) owns focusedInput as a controlled value. If it
      // has since cleared it - which it does the instant an async cart/storefront/
      // rental-default prefill resolves, to collapse the calendar around the applied
      // dates - this queued callback is acting on a focus that no longer holds, and
      // applying it would reopen the calendar and re-notify onFocus 300ms after the
      // fact. Re-check at fire time rather than only at schedule time, since the
      // clear frequently lands in between the two.
      if (!this.props.focusedInput) {
        this.debounceTimer = null;
        return;
      }
      this.setState({ focusedInput });
      this.props.input.onFocus(focusedInput);
      if (typeof this.props.onFocus == 'function' && this.props.onFocus) {
        this.props.onFocus(focusedInput);
      };
    }, 300);
  }

  render() {
    /* eslint-disable no-unused-vars */
    const {
      className,
      rootClassName,
      isDaily,
      startDateId,
      startDateLabel,
      endDateId,
      endDateLabel,
      input,
      meta,
      useMobileMargins,
      // Extract focusedInput and onFocusedInputChange so that
      // the same values will not be passed on to subcomponents.
      focusedInput,
      onFocusedInputChange,
      seatsArray,
      seatsLabel,
      intl,
      quantityAutofocus,
      maxAvailableSeats,
      isCalendarSticky,
      hideSeatsSelection,
      ...rest
    } = this.props;
    /* eslint-disable no-unused-vars */

    if (startDateLabel && !startDateId) {
      throw new Error('startDateId required when a startDateLabel is given');
    }

    if (endDateLabel && !endDateId) {
      throw new Error('endDateId required when a endDateLabel is given');
    }

    // If startDate is valid label changes color and bottom border changes color too
    const startDateLabelClasses = classNames(css.startDateLabel);

    // If endDate is valid label changes color and bottom border changes color too
    const endDateLabelClasses = classNames(css.endDateLabel);

    const label =
      startDateLabel && endDateLabel ? (
        <div className={classNames(css.labels, { [css.mobileMargins]: useMobileMargins })}>
          <label className={startDateLabelClasses} htmlFor={startDateId}>
            {startDateLabel}
          </label>
          <label className={endDateLabelClasses} htmlFor={endDateId}>
            {endDateLabel}
          </label>
        </div>
      ) : null;

    // eslint-disable-next-line no-unused-vars
    const { type, onFocus, onBlur, checked, ...restOfInput } = input;
    const inputProps = {
      isDaily,
      minimumNights: isDaily ? 0 : 1,
      useMobileMargins,
      readOnly: typeof window !== 'undefined' ? window.innerWidth < MAX_MOBILE_SCREEN_WIDTH : false,
      ...restOfInput,
      ...rest,
      onBlur: this.handleBlur,
      onFocus: this.handleFocus,
      focusedInput: this.state.focusedInput,
      startDateId,
      endDateId,
    };
    const classes = classNames(rootClassName || css.fieldRoot, className);
    const errorClasses = classNames({ [css.mobileMargins]: useMobileMargins });

    const maxQuantityAv = +maxAvailableSeats;
    const maxQuantityAvMsg = intl?.formatMessage({ id: "FieldDateRangeInput.maximumQuantityMsg" }, { maxQuantityAv });
    const onlyOneMsg = maxQuantityAv === 1 && Array.isArray(seatsArray)
      ? intl?.formatMessage({ id: 'FieldDateRangeInput.onlyOneAvailable', defaultMessage: 'Only 1 available.' })
      : null;

    const seatsSelectionMaybe =
      seatsArray?.length > 1 && maxQuantityAv > 1 ? (
        <Field
          name="seats"
          type="number"
          validate={maxQuantity(
            intl.formatMessage(
              { id: "FieldDateRangeInput.invalidQuantity" },
              { maxQuantityAv }
            ),
            maxQuantityAv
          )}
        >
          {({ input: fieldInput, meta }) => {
            const [isFocused, setIsFocused] = React.useState(false);
            const [displayValue, setDisplayValue] = React.useState(fieldInput.value || '1');

            React.useEffect(() => {
              if (!isFocused) {
                setDisplayValue(fieldInput.value || '1');
              }
            }, [fieldInput.value, isFocused]);

            return (
              <div className={css.quantityInputContainer}>
                <label className={css.quantityLabel} htmlFor="seats">
                  {seatsLabel}
                </label>
                <div className={classNames(css.quantityWrapper, { [css.quantityWrapperFocused]: isFocused })}>
                  <button
                    type="button"
                    className={css.quantityButton}
                    onClick={() => {
                      const currentValue = parseInt(fieldInput.value || '1', 10);
                      if (currentValue > 1) {
                        fieldInput.onChange((currentValue - 1).toString());
                      }
                    }}
                    aria-label="Decrease quantity"
                  >
                    -
                  </button>
                  <input
                    {...fieldInput}
                    id="seats"
                    data-testid="quantity-value"
                    className={css.quantityInput}
                    value={displayValue}
                    min="1"
                    max={maxQuantityAv}
                    onChange={(e) => {
                      const value = e.target.value;
                      setDisplayValue(value);
                      if (value === '' || (parseInt(value, 10) >= 1 && parseInt(value, 10) <= maxQuantityAv)) {
                        fieldInput.onChange(e);
                      }
                    }}
                    onFocus={(e) => {
                      fieldInput.onFocus(e);
                      setIsFocused(true);
                      setDisplayValue('');
                    }}
                    onBlur={(e) => {
                      fieldInput.onBlur(e);
                      setIsFocused(false);
                      if (e.target.value === '') {
                        fieldInput.onChange('1');
                        setDisplayValue('1');
                      }
                    }}
                    aria-label="Quantity"
                  />
                  <button
                    type="button"
                    className={css.quantityButton}
                    onClick={() => {
                      const currentValue = parseInt(fieldInput.value || '1', 10);
                      if (currentValue < maxQuantityAv) {
                        fieldInput.onChange((currentValue + 1).toString());
                      }
                    }}
                    aria-label="Increase quantity"
                  >
                    +
                  </button>
                </div>
                {maxQuantityAvMsg && (
                  <span className={css.maxQuantityMsg}>{maxQuantityAvMsg}</span>
                )}
                {meta.error && meta.touched && (
                  <span className={css.error}>{meta.error}</span>
                )}
              </div>
            );
          }}
        </Field>
      ) : <div className={css.onlyOneMsg}>{onlyOneMsg}</div>;




    return (
      <div className={classes}>
        {label}
        <DateRangeInput {...inputProps} />
        <ValidationError className={errorClasses} fieldMeta={meta} />
        {!isCalendarSticky && !hideSeatsSelection && seatsSelectionMaybe}
      </div>
    );
  }
}

FieldDateRangeInputComponent.defaultProps = {
  className: null,
  rootClassName: null,
  useMobileMargins: false,
  endDateId: null,
  endDateLabel: null,
  endDatePlaceholderText: null,
  startDateId: null,
  startDateLabel: null,
  startDatePlaceholderText: null,
  focusedInput: null,
  onFocusedInputChange: null,
  maxAvailableSeats: 1,
  hideSeatsSelection: false,
};

FieldDateRangeInputComponent.propTypes = {
  className: string,
  rootClassName: string,
  isDaily: bool.isRequired,
  useMobileMargins: bool,
  endDateId: string,
  endDateLabel: string,
  endDatePlaceholderText: string,
  startDateId: string,
  startDateLabel: string,
  startDatePlaceholderText: string,
  input: object.isRequired,
  meta: object.isRequired,
  focusedInput: oneOf([START_DATE, END_DATE]),
  onFocusedInputChange: func,
  hideSeatsSelection: bool,

  isOutsideRange: func.isRequired,
  firstDayOfWeek: number.isRequired,
};

const FieldDateRangeInput = props => {
  const config = useConfiguration();
  const { isOutsideRange, firstDayOfWeek, ...rest } = props;

  // Outside range -><- today ... today+available days -1 -><- outside range
  const defaultIsOutSideRange = day => {
    const endOfRange = config.stripe.dayCountAvailableForBooking - 1;
    return (
      !isInclusivelyAfterDay(day, moment()) ||
      !isInclusivelyBeforeDay(day, moment().add(endOfRange, 'days'))
    );
  };
  const defaultFirstDayOfWeek = config.localization.firstDayOfWeek;
  return (
    <Field
      component={FieldDateRangeInputComponent}
      isOutsideRange={isOutsideRange || defaultIsOutSideRange}
      firstDayOfWeek={firstDayOfWeek || defaultFirstDayOfWeek}
      {...rest}
    />
  );
};

export { DateRangeInput };
export default FieldDateRangeInput;
